import { useQuery } from '@tanstack/react-query';
import { api } from '../services/api.js';
import { Layout } from '../components/Layout.js';

interface Achievement {
  key: string;
  name: string;
  description: string;
  earned: boolean;
  unlockedAt: string | null;
}

interface UserStats {
  totalXp: number;
  level: number;
  levelTitle: string;
  nextLevelXp: number | null;
  weekStreak: number;
  longestStreak: number;
  totalPlans: number;
}

const LEVEL_XP = [0, 150, 400, 900, 1800, 3500, 6000];

function XpBar({ stats }: { stats: UserStats }) {
  const currentLevelXp = LEVEL_XP[stats.level - 1] ?? 0;
  const nextLevelXp = stats.nextLevelXp ?? LEVEL_XP[LEVEL_XP.length - 1];
  const progress = nextLevelXp > currentLevelXp
    ? Math.min(100, ((stats.totalXp - currentLevelXp) / (nextLevelXp - currentLevelXp)) * 100)
    : 100;

  return (
    <div className="card p-5 mb-6">
      <div className="flex items-center justify-between mb-2">
        <div>
          <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Level {stats.level}</span>
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">{stats.levelTitle}</h2>
        </div>
        <div className="text-right">
          <p className="text-2xl font-bold" style={{ color: 'var(--accent)' }}>{stats.totalXp.toLocaleString()} XP</p>
          {stats.nextLevelXp && (
            <p className="text-xs text-gray-400 dark:text-gray-500">{stats.nextLevelXp.toLocaleString()} to next level</p>
          )}
        </div>
      </div>
      <div className="w-full bg-gray-100 dark:bg-gray-700 rounded-full h-2 overflow-hidden">
        <div
          className="h-2 rounded-full transition-all duration-500"
          style={{ width: `${progress}%`, background: 'var(--accent)' }}
        />
      </div>
      <div className="flex gap-6 mt-4 text-center">
        <div className="flex-1">
          <p className="text-xl font-bold text-gray-900 dark:text-white">{stats.weekStreak}</p>
          <p className="text-xs text-gray-400 dark:text-gray-500">week streak</p>
        </div>
        <div className="flex-1">
          <p className="text-xl font-bold text-gray-900 dark:text-white">{stats.longestStreak}</p>
          <p className="text-xs text-gray-400 dark:text-gray-500">longest streak</p>
        </div>
        <div className="flex-1">
          <p className="text-xl font-bold text-gray-900 dark:text-white">{stats.totalPlans}</p>
          <p className="text-xs text-gray-400 dark:text-gray-500">plans created</p>
        </div>
      </div>
    </div>
  );
}

export function AchievementsPage() {
  const { data: achievements = [], isLoading: achLoading } = useQuery<Achievement[]>({
    queryKey: ['achievements'],
    queryFn: () => api.get('/api/achievements'),
    staleTime: 5 * 60_000,
  });

  const { data: stats, isLoading: statsLoading } = useQuery<UserStats>({
    queryKey: ['stats'],
    queryFn: () => api.get('/api/stats'),
    staleTime: 60_000,
  });

  const earned = achievements.filter((a) => a.earned);
  const locked = achievements.filter((a) => !a.earned);

  return (
    <Layout>
      <h1 className="text-2xl font-semibold text-gray-900 dark:text-white mb-6">Achievements</h1>

      {stats && !statsLoading && <XpBar stats={stats} />}

      {achLoading ? (
        <div className="grid sm:grid-cols-3 gap-3 animate-pulse">
          {Array.from({ length: 6 }, (_, i) => (
            <div key={i} className="h-24 bg-gray-200 dark:bg-gray-700 rounded-xl" />
          ))}
        </div>
      ) : (
        <>
          {earned.length > 0 && (
            <>
              <h2 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-3">
                Earned ({earned.length})
              </h2>
              <div className="grid sm:grid-cols-3 gap-3 mb-8">
                {earned.map((a) => (
                  <div
                    key={a.key}
                    className="card p-4"
                    style={{ borderColor: 'var(--accent)' }}
                  >
                    <p className="text-2xl mb-1">🏆</p>
                    <p className="text-sm font-semibold text-gray-900 dark:text-white">{a.name}</p>
                    <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">{a.description}</p>
                    {a.unlockedAt && (
                      <p className="text-[10px] text-gray-300 dark:text-gray-600 mt-1">
                        {new Date(a.unlockedAt).toLocaleDateString()}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}

          {locked.length > 0 && (
            <>
              <h2 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-3">
                Locked ({locked.length})
              </h2>
              <div className="grid sm:grid-cols-3 gap-3">
                {locked.map((a) => (
                  <div
                    key={a.key}
                    className="bg-gray-50 dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-700 p-4 opacity-60"
                  >
                    <p className="text-2xl mb-1 grayscale">🏆</p>
                    <p className="text-sm font-semibold text-gray-500 dark:text-gray-400">{a.name}</p>
                    <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">{a.description}</p>
                  </div>
                ))}
              </div>
            </>
          )}
        </>
      )}
    </Layout>
  );
}
